import { FC, useState } from 'react';
import { Button, Grid, IconButton, InputAdornment, TextField, Typography } from '@mui/material';
import { useForm } from '@tanstack/react-form';
import { observer } from 'mobx-react';
import { AxiosError } from 'axios';
import { Visibility } from '@mui/icons-material';
import BaseConfig from './BaseConfig';
import { useCreatePersonalkollenIntegration } from '../../../stores/FetcherStores/integrationFetcher';
import useCompanyId from '../../../hooks/useCompanyId';
import { usePrices, useResellerPrices } from '../../../stores/FetcherStores/PricesFetcher';
import { useGetCompany } from '../../../stores/FetcherStores/CompanyFetcher';

interface PersonalkollenConfigProps {
  onClose: () => void;
}

const PersonalkollenConfig: FC<PersonalkollenConfigProps> = observer(({ onClose }) => {
  const companyId = useCompanyId();
  const { data: company } = useGetCompany(companyId);
  const {
    mutateAsync: createPersonalkollenIntegration,
    isLoading: isCreating,
    error,
  } = useCreatePersonalkollenIntegration();
  const { data: prices } = usePrices();
  const { data: resellerPrices } = useResellerPrices(company?.resellerId || '');
  const [showApiKey, setShowApiKey] = useState(false);

  const form = useForm({
    defaultValues: {
      apiKey: '',
    },
    onSubmit: async ({ value }) => {
      await createPersonalkollenIntegration({
        companyId,
        apiKey: value.apiKey,
      })
        .then(() => {
          onClose();
        })
        .catch(() => {});
    },
  });

  return (
    <BaseConfig
      title="Personalkollen"
      description="Personalkollen är en lösning för att hantera personal och löner. Med Enklakassan integrationen kan du få in dricks och försäljningsstatistik direkt in i Personalkollen. Ange API-nyckel för att aktivera integrationen."
      price={`${prices?.subscriptions.PERSONALKOLLEN.price.format()}/mån`}
      resellerPrice={
        resellerPrices ? `${resellerPrices?.subscriptions.PERSONALKOLLEN.price.format()}/mån` : ''
      }
      onClose={onClose}
      onSubmit={() => form.handleSubmit()}
      errorMessage={
        error instanceof AxiosError ? error?.response?.data?.name || error?.message : error?.message
      }
      buttons={
        <>
          <Button variant="outlined" onClick={onClose} color="secondary">
            Avbryt
          </Button>
          <Button
            type="submit"
            variant="contained"
            disabled={form.state.isSubmitting || isCreating}
            sx={{
              backgroundColor: '#1db954',
              '&:hover': {
                backgroundColor: '#1a9f4a',
              },
            }}
          >
            {form.state.isSubmitting || isCreating ? 'Aktiverar...' : 'Aktivera Personalkollen'}
          </Button>
        </>
      }
    >
      <Grid item xs={12}>
        <Typography variant="h6" gutterBottom sx={{ mt: 2, mb: 2 }}>
          API-nyckel
        </Typography>
      </Grid>

      <form.Field
        name="apiKey"
        validators={{
          onChange: ({ value }) => {
            if (!value || value.trim() === '') {
              return 'API-nyckel är obligatorisk';
            }
            return undefined;
          },
        }}
      >
        {(field) => (
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="API-nyckel"
              value={field.state.value}
              onChange={(e) => field.handleChange(e.target.value)}
              error={field.state.meta.errors.length > 0}
              helperText={
                field.state.meta.errors.length > 0
                  ? field.state.meta.errors.join(', ')
                  : 'Ange Personalkollen API-nyckel'
              }
              type={showApiKey ? 'text' : 'password'}
              placeholder="sk-..."
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => {
                        setShowApiKey(!showApiKey);
                      }}
                    >
                      <Visibility />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
        )}
      </form.Field>
    </BaseConfig>
  );
});

export default PersonalkollenConfig;
